import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { useHistory } from '../../lib/useHistory.js';
import { EDITOR_MODULES, blankDocument, blankRegion, blankWidget, nextId, validateLayout } from '../../lib/layoutSchema.js';
import { findTemplate, templateDocument } from '../../lib/layoutTemplates.js';
import { moveItem, resizeCanvas, restack } from '../../lib/regionGeometry.js';
import { useToast } from '../../components/Toast.js';
import type { LayoutDocument, LayoutRegion, LayoutWidget } from '../../lib/types.js';

/**
 * Everything the layout editor edits, shared by the desktop and mobile shells:
 * the document (with undo/redo), the selection, and save/publish. Keeping it
 * above both shells means crossing the mobile breakpoint loses nothing.
 */
export function useLayoutEditor(id: string | undefined) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const template = findTemplate(params.get('template'));
  const toast = useToast();
  const media = useQuery(() => api.media(), []);
  const modules = useQuery(() => api.modules(), []);
  const layouts = useQuery(() => api.layouts(), []);
  const menuBoards = useQuery(() => api.menuBoards(), []);
  const existing = useQuery(() => (id ? api.layout(id) : Promise.resolve(null)), [id]);

  const installedModules = useMemo(
    () => (modules.data?.data ?? []).filter((m) => m.installed && m.enabled),
    [modules.data],
  );
  const moduleChoices = useMemo(
    () => [...new Set([...EDITOR_MODULES, ...installedModules.map((m) => m.slug)])],
    [installedModules],
  );
  const moduleBySlug = useMemo(() => new Map(installedModules.map((m) => [m.slug, m])), [installedModules]);

  const history = useHistory<LayoutDocument | null>(id ? null : templateDocument(template?.key));
  const doc = history.value;
  const [name, setName] = useState(template?.label ?? 'Untitled layout');
  const [saving, setSaving] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  // What was last loaded or saved, to tell whether there are unsaved edits.
  const [saved, setSaved] = useState<{ doc: LayoutDocument | null; name: string }>(() => ({
    doc: id ? null : history.value,
    name: template?.label ?? 'Untitled layout',
  }));
  const loadedFor = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    const detail = existing.data;
    if (!detail) return;
    const next = detail.latestDocument ?? blankDocument(detail.width, detail.height);
    setName(detail.name);
    setSaved({ doc: next, name: detail.name });
    // A fresh layout starts its history over; a refetch after saving just
    // takes the server's copy and keeps the undo steps.
    if (loadedFor.current === id) history.replace(next);
    else history.reset(next);
    loadedFor.current = id;
    // history's actions are stable, so only a new id or fetch matters here.
  }, [id, existing.data]);

  const issues = useMemo(() => (doc ? validateLayout(doc) : []), [doc]);
  const dirty = doc !== saved.doc || name !== saved.name;
  const selectedIndex = doc ? doc.regions.findIndex((r) => r.id === selectedRegion) : -1;

  const edit = (update: (d: LayoutDocument) => LayoutDocument, key?: string): void =>
    history.set((d) => (d ? update(d) : d), key);

  const mapRegion = (ri: number, fn: (r: LayoutRegion) => LayoutRegion, key?: string): void =>
    edit((d) => ({ ...d, regions: d.regions.map((r, i) => (i === ri ? fn(r) : r)) }), key);

  const mapItems = (ri: number, fn: (items: LayoutWidget[]) => LayoutWidget[], key?: string): void =>
    mapRegion(ri, (r) => ({ ...r, playlist: { ...r.playlist, items: fn(r.playlist.items) } }), key);

  function patchDoc(next: Partial<LayoutDocument>, key?: string): void {
    edit((d) => ({ ...d, ...next }), key);
  }

  /** Typed sizes change the canvas only (regions would collapse mid-keystroke); the orientation follows its shape. */
  function setCanvasSize(width: number, height: number): void {
    patchDoc({ dimensions: { width, height, orientation: width >= height ? 'landscape' : 'portrait' } }, 'canvas-size');
  }

  /** Turning the canvas swaps its sides and carries the regions along. */
  function setOrientation(o: 'landscape' | 'portrait'): void {
    edit((d) => {
      const { width, height } = d.dimensions;
      return (width >= height ? 'landscape' : 'portrait') === o ? d : resizeCanvas(d, height, width);
    });
  }

  function resizeTo(width: number, height: number): void {
    edit((d) => resizeCanvas(d, width, height));
  }

  function patchRegion(ri: number, next: Partial<LayoutRegion>, key?: string): void {
    mapRegion(ri, (r) => ({ ...r, ...next }), key);
  }

  function patchWidget(ri: number, wi: number, next: Partial<LayoutWidget>, key?: string): void {
    mapItems(ri, (items) => items.map((w, j) => (j === wi ? { ...w, ...next } : w)), key);
  }

  function moveWidget(fromRi: number, fromWi: number, toRi: number, toIndex: number): void {
    edit((d) => {
      const source = d.regions[fromRi];
      const target = d.regions[toRi];
      if (!source || !target) return d;
      const moved = moveItem(source.playlist.items, fromWi, target.playlist.items, toIndex);
      const regions = d.regions.map((r, i) => {
        if (i === toRi) return { ...r, playlist: { ...r.playlist, items: moved.to } };
        if (i === fromRi) return { ...r, playlist: { ...r.playlist, items: moved.from } };
        return r;
      });
      return { ...d, regions };
    });
  }

  /** Moves a widget one place up (-1) or down (+1) its own playlist. */
  function moveWidgetBy(ri: number, wi: number, by: -1 | 1): void {
    moveWidget(ri, wi, ri, by < 0 ? wi - 1 : wi + 2);
  }

  /** Appends a widget and returns its id. */
  function addWidget(ri: number, module: string): string {
    const widget = blankWidget(module, moduleBySlug.get(module)?.defaults);
    mapItems(ri, (items) => [...items, widget]);
    return widget.id;
  }

  function duplicateWidget(ri: number, wi: number): void {
    mapItems(ri, (items) => {
      const w = items[wi];
      if (!w) return items;
      const copy = { ...structuredClone(w), id: nextId('w') };
      return [...items.slice(0, wi + 1), copy, ...items.slice(wi + 1)];
    });
  }

  function removeWidget(ri: number, wi: number): void {
    mapItems(ri, (items) => items.filter((_, i) => i !== wi));
  }

  /** Adds a full-canvas region on top and selects it. */
  function addRegion(): void {
    if (!doc) return;
    const top = Math.max(0, ...doc.regions.map((r) => r.zIndex ?? 0));
    const region = { ...blankRegion(doc.dimensions.width, doc.dimensions.height), zIndex: doc.regions.length ? top + 1 : 0 };
    edit((d) => ({ ...d, regions: [...d.regions, region] }));
    setSelectedRegion(region.id);
  }

  /** Copies a region (and its playlist) slightly offset, on top, and selects the copy. */
  function duplicateRegion(ri: number): void {
    const source = doc?.regions[ri];
    if (!doc || !source) return;
    const { rect } = source;
    const percent = rect.unit === 'percent';
    const maxW = percent ? 100 : doc.dimensions.width;
    const maxH = percent ? 100 : doc.dimensions.height;
    const step = percent ? 3 : Math.round(doc.dimensions.width * 0.03);
    const copy: LayoutRegion = {
      ...structuredClone(source),
      ...(source.name ? { name: `${source.name} copy` } : {}),
      id: nextId('r'),
      zIndex: Math.max(0, ...doc.regions.map((r) => r.zIndex ?? 0)) + 1,
      rect: {
        ...rect,
        x: Math.min(rect.x + step, Math.max(maxW - rect.width, 0)),
        y: Math.min(rect.y + step, Math.max(maxH - rect.height, 0)),
      },
    };
    copy.playlist = {
      ...copy.playlist,
      id: nextId('pl'),
      items: copy.playlist.items.map((w) => ({ ...w, id: nextId('w') })),
    };
    edit((d) => ({ ...d, regions: [...d.regions, copy] }));
    setSelectedRegion(copy.id);
  }

  function removeRegion(ri: number): void {
    if (!doc || doc.regions.length <= 1) return;
    const gone = doc.regions[ri]?.id;
    edit((d) => ({ ...d, regions: d.regions.filter((_, i) => i !== ri) }));
    if (gone && gone === selectedRegion) setSelectedRegion(null);
  }

  function restackRegion(regionId: string, to: 'forward' | 'backward' | 'front' | 'back'): void {
    edit((d) => {
      const regions = restack(d.regions, regionId, to);
      return regions === d.regions ? d : { ...d, regions };
    });
  }

  async function save(): Promise<string | null> {
    if (!doc) return null;
    setSaving(true);
    try {
      const result = id ? await api.updateLayout(id, name.trim(), doc) : await api.createLayout(name.trim(), doc);
      setSaved({ doc, name });
      toast.push('Saved', 'success');
      if (!id) navigate(`/layouts/${result.id}`, { replace: true });
      else existing.refetch();
      return result.id;
    } catch (err) {
      if (err instanceof ApiError && err.errors.document) toast.error(new Error(err.errors.document.join('; ')));
      else toast.error(err);
      return null;
    } finally {
      setSaving(false);
    }
  }

  /** Saves, then publishes. A layout that was never saved is created first. */
  async function publish(): Promise<void> {
    if (!doc) return;
    setSaving(true);
    try {
      const layoutId = id ?? (await api.createLayout(name.trim(), doc)).id;
      if (id) await api.updateLayout(id, name.trim(), doc);
      await api.publishLayout(layoutId);
      setSaved({ doc, name });
      toast.push('Published', 'success');
      if (!id) navigate(`/layouts/${layoutId}`, { replace: true });
      else existing.refetch();
    } catch (err) {
      toast.error(err instanceof ApiError ? new Error(err.fieldMessage() ?? err.message) : err);
    } finally {
      setSaving(false);
    }
  }

  /** Back to the layouts list, asking first if that would drop unsaved edits. */
  function leave(): void {
    if (dirty && !window.confirm('Leave without saving your changes?')) return;
    navigate('/layouts');
  }

  return {
    id,
    existing,
    doc,
    name,
    setName,
    saving,
    dirty,
    issues,
    media: media.data ?? [],
    layouts: layouts.data ?? [],
    menuBoards: menuBoards.data ?? [],
    moduleChoices,
    moduleBySlug,
    selectedRegion,
    selectedIndex,
    setSelectedRegion,
    history,
    patchDoc,
    setCanvasSize,
    setOrientation,
    resizeTo,
    patchRegion,
    patchWidget,
    moveWidget,
    moveWidgetBy,
    addWidget,
    duplicateWidget,
    removeWidget,
    addRegion,
    duplicateRegion,
    removeRegion,
    restackRegion,
    save,
    publish,
    leave,
  };
}

export type LayoutEditorState = ReturnType<typeof useLayoutEditor>;
